// ── Qué muestra el inspector para el elemento seleccionado ───────────────────
//
// Lógica pura, sin DOM ni React: acá vive lo que puede equivocarse (qué valor
// está puesto, de dónde sale, qué pasa si el template no puede expresar algo).

export type UtilityGroupId =
  | 'align' | 'textSize' | 'textColor' | 'padding' | 'margin' | 'gap' | 'size';

export interface UtilityGroup {
  id: UtilityGroupId;
  options: string[];
}

/** De dónde sale el valor que se está viendo. */
export type ValueOrigin =
  /** Una clase que puso una edición manual. */
  | 'manual'
  /** Una clase que ya traía la página cuando se generó. */
  | 'template'
  /** Ninguna clase de este grupo: manda el CSS del componente. */
  | 'inherited';

export interface GroupState {
  id: UtilityGroupId;
  options: string[];
  /** Clase puesta hoy, o null si no hay ninguna de este grupo. */
  value: string | null;
  origin: ValueOrigin;
  /**
   * true si el template no tiene ninguna utility para esta propiedad. El panel lo
   * dice explícitamente en vez de mostrar un control vacío: enterarse de que el
   * control no aplica es mejor que terminar con la página despegada del sistema.
   */
  unavailable: boolean;
}

/**
 * Estado de cada grupo para un elemento.
 *
 * @param classes    clases que tiene el elemento ahora
 * @param manualSet  clases de ESTE elemento que puso una edición manual y siguen puestas
 */
export function inspectorState(
  groups: UtilityGroup[],
  classes: string[],
  manualSet: ReadonlySet<string>,
): GroupState[] {
  return groups.map(({ id, options }) => {
    // Si por lo que sea hay dos del mismo grupo, gana la última del atributo:
    // es la que el navegador aplica cuando ambas definen la misma propiedad.
    let value: string | null = null;
    for (const c of classes) if (options.includes(c)) value = c;

    return {
      id,
      options,
      value,
      origin: value === null ? 'inherited' : manualSet.has(value) ? 'manual' : 'template',
      unavailable: options.length === 0,
    };
  });
}

/**
 * Ops para pasar de la clase actual a la nueva dentro de un grupo.
 *
 * Se quita la anterior del MISMO grupo, no todas: las clases de otros grupos y
 * las del componente no son asunto de este control.
 */
export function classChangeOps(
  group: GroupState,
  next: string | null,
): { add: string[]; remove: string[] } {
  const add = next && next !== group.value ? [next] : [];
  const remove = group.value && group.value !== next ? [group.value] : [];
  return { add, remove };
}
